/**
 * ErrorBoundary global pour éviter l'écran noir en cas d'erreur React.
 *
 * Cause #1 historiquement : Google Translate / Edge Translate qui injecte
 * des balises <font> dans le DOM et casse la réconciliation React
 * (« Failed to execute 'insertBefore' on 'Node' »). Le `<meta name="google"
 * content="notranslate">` dans index.html corrige la cause. Ce boundary est
 * un filet de sécurité : si l'erreur survient quand même, on affiche un
 * écran de récupération propre au lieu d'un écran blanc/noir.
 */
import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  /** Message à afficher en plus du message d'erreur générique. */
  context?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log pour debug serveur si besoin (console only pour l'instant)
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  reload = () => {
    window.location.reload();
  };

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || 'Erreur inconnue';
    const isInsertBefore = msg.includes('insertBefore') || msg.includes('removeChild');

    return (
      <div className="min-h-[40vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-xl shadow p-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/40 mb-3">
            <AlertTriangle size={22} className="text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Une erreur d'affichage est survenue
          </h2>
          {this.props.context && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{this.props.context}</p>
          )}
          {isInsertBefore && (
            <div className="mt-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2">
              Si vous avez activé la <strong>traduction automatique</strong> du navigateur
              (Google Translate, Edge Translate), désactivez-la sur ce site puis rechargez.
            </div>
          )}
          <details className="mt-3 text-left">
            <summary className="text-xs text-gray-500 cursor-pointer">Détails techniques</summary>
            <pre className="mt-2 text-[10px] text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto max-h-32">
              {msg}
            </pre>
          </details>
          <div className="flex items-center justify-center gap-2 mt-4">
            <button
              onClick={this.reload}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded"
            >
              <RefreshCw size={14} /> Recharger la page
            </button>
            <button
              onClick={this.reset}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Réessayer sans recharger
            </button>
          </div>
        </div>
      </div>
    );
  }
}
